import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'shared/api/client';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { draftKeys } from 'entities/draft';
import { documentKeys } from 'entities/document';
import { styleKeys } from 'entities/style';
import { themeKeys } from './keys';

type ApplyScope = 'full' | 'variables-only';
type ConflictStrategy = 'overwrite' | 'keep';

export function useThemes(documentSuperId: number | string | undefined) {
  return useQuery({
    queryKey: themeKeys.themes(documentSuperId),
    queryFn: async () => {
      const { data } = await api.GET('/themes/', { params: { query: { documentSuperId: Number(documentSuperId) } } });
      return data;
    },
    enabled: !!documentSuperId,
  });
}

export function useThemeDiff({ themeId, documentSuperId, enabled = true }: { themeId?: string; documentSuperId?: number | string; enabled?: boolean }) {
  return useQuery({
    queryKey: themeKeys.diff(themeId, documentSuperId),
    queryFn: async () => {
      const { data } = await api.GET('/themes/{id}/diff', {
        params: { path: { id: themeId as string }, query: { documentSuperId: Number(documentSuperId) } },
      });
      return data;
    },
    enabled: enabled && !!themeId && !!documentSuperId,
  });
}

export function useAppliedThemeStatus(documentSuperId: number | string | undefined) {
  return useQuery({
    queryKey: themeKeys.appliedStatus(documentSuperId),
    queryFn: async () => {
      const { data } = await api.GET('/themes/applied-status', { params: { query: { documentSuperId: Number(documentSuperId) } } });
      return data;
    },
    enabled: !!documentSuperId,
  });
}

export function useCreateTheme() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ documentSuperId, name }: { documentSuperId: number | string; name: string }) => {
      const { data } = await api.POST('/themes/', { body: { documentSuperId: Number(documentSuperId), name } });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: themeKeys.themesAll });
      queryClient.invalidateQueries({ queryKey: themeKeys.appliedStatusAll });
      queryClient.invalidateQueries({ queryKey: [...documentKeys.detailAll] });
    },
    onError: ServerErrorHandler,
  });
}

export function useRenameTheme() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ themeId, documentSuperId, name }: { themeId: string; documentSuperId: number | string; name: string }) => {
      const { data } = await api.PATCH('/themes/{id}', { params: { path: { id: themeId } }, body: { documentSuperId: Number(documentSuperId), name } });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: themeKeys.themesAll }),
    onError: ServerErrorHandler,
  });
}

export function usePromoteTheme() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ themeId }: { themeId: string }) => {
      const { data } = await api.POST('/themes/{id}/promote', { params: { path: { id: themeId } }, body: {} });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: themeKeys.themesAll }),
    onError: ServerErrorHandler,
  });
}

export function useDemoteTheme() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ themeId, documentSuperId }: { themeId: string; documentSuperId: number | string }) => {
      const { data } = await api.POST('/themes/{id}/demote', { params: { path: { id: themeId } }, body: { documentSuperId: Number(documentSuperId) } });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: themeKeys.themesAll }),
    onError: ServerErrorHandler,
  });
}

export function useDeleteTheme() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ themeId, documentSuperId }: { themeId: string; documentSuperId: number | string }) => {
      const { data } = await api.DELETE('/themes/{id}', { params: { path: { id: themeId }, query: { documentSuperId: Number(documentSuperId) } } });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: themeKeys.themesAll }),
    onError: ServerErrorHandler,
  });
}

export function useApplyTheme() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      themeId,
      documentSuperId,
      applyScope,
      conflictStrategy,
    }: {
      themeId: string;
      documentSuperId: number | string;
      applyScope?: ApplyScope;
      conflictStrategy?: ConflictStrategy;
    }) => {
      const { data } = await api.POST('/themes/{id}/apply', {
        params: { path: { id: themeId } },
        body: { documentSuperId: Number(documentSuperId), applyScope, conflictStrategy },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...draftKeys.all] });
      queryClient.invalidateQueries({ queryKey: [...documentKeys.detailAll] });
      queryClient.invalidateQueries({ queryKey: [...styleKeys.cssAll] });
      queryClient.invalidateQueries({ queryKey: themeKeys.appliedStatusAll });
    },
    onError: ServerErrorHandler,
  });
}
